import React from 'react';
import { Link } from 'react-router-dom';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'navy' | 'outline-light' | 'outline-dark' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  to?: string;
  href?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  to,
  href,
  icon,
  iconPosition = 'right',
  className = '',
  ...rest
}) => {
  const sizeClasses = {
    sm: 'px-4 py-2 text-xs',
    md: 'px-5 py-3 text-sm',
    lg: 'px-7 py-3.5 text-base',
  }[size];

  const variantClasses = {
    primary:
      'bg-spaq-cyan text-white font-semibold hover:bg-spaq-cyan-dark shadow-cyan-glow border border-spaq-cyan transition-all duration-200',
    navy:
      'bg-navy text-white font-semibold hover:bg-navy-light shadow-soft border border-navy transition-all duration-200',
    secondary:
      'bg-surface-sky text-navy font-semibold hover:bg-spaq-cyan-pale border border-spaq-cyan/30 transition-all duration-200',
    'outline-light':
      'bg-white/10 text-white font-medium border border-white/30 hover:bg-white hover:text-navy transition-all duration-200',
    'outline-dark':
      'bg-white text-navy font-semibold border border-surface-border hover:border-spaq-cyan hover:text-spaq-cyan-dark shadow-soft transition-all duration-200',
    ghost:
      'bg-transparent text-spaq-cyan-dark font-semibold hover:text-navy transition-all duration-200 p-0',
  }[variant];

  const baseClasses = `inline-flex items-center justify-center gap-2 rounded-xl transition-all cursor-pointer select-none ${sizeClasses} ${variantClasses} ${className}`;

  const content = (
    <>
      {icon && iconPosition === 'left' && <span className="inline-flex shrink-0">{icon}</span>}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <span className="inline-flex shrink-0">{icon}</span>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={baseClasses}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} className={baseClasses} target="_blank" rel="noopener noreferrer">
        {content}
      </a>
    );
  }

  return (
    <button className={baseClasses} {...rest}>
      {content}
    </button>
  );
};
